/*
 * Copyright © 2021-present Protonbase Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Protonbase Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Protonbase Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { Radio } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import * as echarts from 'echarts/core'
import styles from './index.less'
import { ALL_PAGES } from '@/constants'
import { useTranslateAmount } from '@/hooks/useTranslateAmount'
import { LayoutOthers } from '@/layouts/LayoutOthers'
import { ReactEcharts } from '@/libs/ReactEcharts'
import { accountClient } from '@/services/cloudClient/accountClient'
import { timeFormatter } from '@/utils/timeFormatter'

export function StatementTrend() {
  const { t } = useTranslation()
  const { contractId } = useParams()
  const { currency, translateAmount } = useTranslateAmount()
  const eChartsInstanceRef = useRef<echarts.ECharts>()
  const [addMonths, setAddMonths] = useState(-6)

  useEffect(() => {
    accountClient.statementApi
      .listStatements(contractId, {
        start: dayjs().add(addMonths, 'months').startOf('month').valueOf(),
        end: dayjs().add(1, 'months').startOf('month').valueOf(),
        ...ALL_PAGES,
      })
      .then(res => {
        eChartsInstanceRef.current?.setOption({
          xAxis: {
            min: dayjs().add(addMonths, 'months').startOf('month').valueOf(),
            max: dayjs().add(-1, 'months').valueOf(),
          },
          series: [
            {
              type: 'bar',
              // label: {
              //   show: true,
              //   position: 'top',
              // },
              barMaxWidth: 40,
              data: res.data.items.map(({ start, amount }) => [dayjs(start).startOf('month').valueOf(), amount]),
            },
          ],
        })
      })
  }, [addMonths])

  return (
    <LayoutOthers.ChartBox title={t('Statement Trend')} className={styles.container}>
      <div className={styles.timeRange}>
        <Radio.Group value={addMonths} onChange={e => setAddMonths(e.target.value)} size="small">
          <Radio.Button value={-6}>{t('Last 6 Months')}</Radio.Button>
          <Radio.Button value={-12}>{t('Last Year')}</Radio.Button>
        </Radio.Group>
      </div>
      <ReactEcharts
        loading={false}
        hideZoomButton={true}
        style={{ height: 225 }}
        baseOption={{
          legend: false,
          tooltip: {
            trigger: 'axis',
            valueFormatter: (value: any) => translateAmount(value),
            axisPointer: {
              label: {
                formatter: (params: any) => timeFormatter.format(params.value, 'yearMonth'),
              },
            },
          },
          xAxis: {
            type: 'time',
            minInterval: 3600 * 24 * 30 * 1000,
            axisLabel: {
              hideOverlap: true,
            },
          },
          yAxis: {
            type: 'value',
            name: currency,
          },
        }}
        onChartReady={instance => {
          eChartsInstanceRef.current = instance
        }}
      />
    </LayoutOthers.ChartBox>
  )
}
